import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

import AirIcon from '@mui/icons-material/Air';
import CloudIcon from '@mui/icons-material/Cloud';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpacityIcon from '@mui/icons-material/Opacity';
import SearchIcon from '@mui/icons-material/Search';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography
} from '@mui/joy';

import OpenMeteoData from '../../models/Weather';
import { TelemetryService } from '../../services/TelemetryService';

// Mock data for weather conditions
const mockWeatherData = {
  currentCondition: 'Partly Cloudy',
  temperature: 22, // Celsius
  windSpeed: 18, // km/h
  windDirection: 'NE',
  humidity: 65, // percentage
  precipitation: 10, // percentage chance
  flightStatus: 'caution', // 'safe', 'caution', 'unsafe'
  locations: [
    {
      name: 'Site Alpha',
      condition: 'Sunny',
      windSpeed: 12,
      status: 'safe'
    },
    {
      name: 'Site Beta',
      condition: 'Cloudy',
      windSpeed: 18,
      status: 'caution'
    },
    {
      name: 'Site Gamma',
      condition: 'Rain',
      windSpeed: 25,
      status: 'unsafe'
    }
  ]
};

// Define location type
interface Location {
  name: string;
  latitude: number;
  longitude: number;
}

// Define forecast data type
interface ForecastData {
  date: string;
  condition: string;
  maxTemperature: number;
  minTemperature: number;
  windSpeed: number;
  precipitation: number;
}

interface WeatherImpactIndicatorProps {
  weatherData?: typeof mockWeatherData;
}

const WeatherImpactIndicator = ({ weatherData: initialWeatherData = mockWeatherData }: WeatherImpactIndicatorProps) => {
  const { t } = useTranslation();
  const [weatherData, setWeatherData] = useState(initialWeatherData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [forecast, setForecast] = useState<ForecastData[]>([]);

  // Search results from geocoding
  const [searchResults, setSearchResults] = useState<Location[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Function to search for locations using Mapbox geocoding
  const searchLocationsImpl = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);

      // Use Mapbox geocoding API (the same one used in TelemetryService.getAddressFromLatLong)
      const mapboxToken = 'pk.eyJ1IjoianVsaWFub2VzIiwiYSI6ImNrOGNwbXVzbTA2bnMzZW95bmhvemIwbGkifQ.vHzrkhopow_IrtZm5yP5iA';
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&limit=5`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const locations: Location[] = data.features.map((feature: any) => ({
          name: feature.place_name,
          longitude: feature.center[0],
          latitude: feature.center[1]
        }));

        setSearchResults(locations);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching for locations:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Function to convert weather code to condition
  const getConditionFromWeatherCode = (code: number): string => {
    if (code === 0) return 'Clear Sky';
    if (code >= 1 && code <= 3) return 'Partly Cloudy';
    if (code >= 45 && code <= 48) return 'Foggy';
    if (code >= 51 && code <= 55) return 'Drizzle';
    if (code >= 56 && code <= 57) return 'Freezing Drizzle';
    if (code >= 61 && code <= 65) return 'Rain';
    if (code >= 66 && code <= 67) return 'Freezing Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Rain Showers';
    if (code >= 85 && code <= 86) return 'Snow Showers';
    if (code === 95) return 'Thunderstorm';
    if (code >= 96 && code <= 99) return 'Thunderstorm with Hail';
    return 'Unknown';
  };

  // Function to determine flight status based on weather conditions
  const getFlightStatusFromWeather = (weatherCode: number, windSpeed: number): 'safe' | 'caution' | 'unsafe' => {
    // Unsafe conditions: Thunderstorms, heavy rain, snow, strong winds
    if (weatherCode >= 95 || windSpeed > 25 ||
        (weatherCode >= 71 && weatherCode <= 77) ||
        weatherCode === 65 || weatherCode === 67 ||
        weatherCode === 82) {
      return 'unsafe';
    }

    // Caution conditions: Moderate rain, fog, moderate winds
    if ((weatherCode >= 61 && weatherCode <= 63) ||
        (weatherCode >= 45 && weatherCode <= 48) ||
        (windSpeed > 15 && windSpeed <= 25)) {
      return 'caution';
    }

    // Safe conditions: Clear, partly cloudy, light winds
    return 'safe';
  };

  // Function to convert Celsius to Fahrenheit
  const celsiusToFahrenheit = (celsius: number): number => (celsius * 9/5) + 32;

  // Function to get wind direction from degrees
  const getWindDirection = (degrees: number): string => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Function to fetch weather data for a location
  const fetchWeatherData = async (location: Location) => {
    try {
      setLoading(true);
      setError(null);

      // Call the TelemetryService.getWeatherDataWithUnits API
      const apiData = await TelemetryService.getWeatherDataWithUnits(
        location.latitude,
        location.longitude,
        'celsius'
      );

      if (!apiData || !apiData.current) {
        throw new Error('Invalid weather data received');
      }

      // Extract current weather data
      const currentWeatherCode = apiData.current.weather_code || 0;
      const currentCondition = getConditionFromWeatherCode(currentWeatherCode);
      const currentTemp = apiData.current.temperature_2m;
      const currentWindSpeed = apiData.current.wind_speed_10m;
      const currentWindDirection = apiData.current.wind_direction_10m;
      const currentHumidity = apiData.current.relative_humidity_2m || 0;
      const currentPrecipitation = apiData.current.precipitation || 0;
      const flightStatus = getFlightStatusFromWeather(currentWeatherCode, currentWindSpeed);

      // Create new weather data object
      const newWeatherData = {
        ...weatherData,
        currentCondition,
        temperature: currentTemp,
        windSpeed: currentWindSpeed,
        windDirection: getWindDirection(currentWindDirection),
        humidity: currentHumidity,
        precipitation: currentPrecipitation,
        flightStatus,
        locations: [
          {
            name: location.name,
            condition: currentCondition,
            windSpeed: currentWindSpeed,
            status: flightStatus
          },
          ...weatherData.locations.slice(0, 2)
        ]
      };

      // Generate forecast data from daily forecast
      const newForecast: ForecastData[] = [];

      if (apiData.daily && apiData.daily.time && apiData.daily.time.length > 0) {
        for (let i = 0; i < Math.min(3, apiData.daily.time.length); i++) {
          const weatherCode = apiData.daily.weather_code?.[i] || 0;
          const maxTemp = apiData.daily.temperature_2m_max?.[i] || 0;
          const minTemp = apiData.daily.temperature_2m_min?.[i] || 0;
          const windSpeed = apiData.daily.wind_speed_10m_max?.[i] || 0;
          const precipitation = apiData.daily.precipitation_probability_max?.[i] || 0;

          // Parse the date string directly from the API (format: "2025-05-09")
          const dateStr = apiData.daily.time[i];
          // Split the date string into parts
          const [year, month, day] = dateStr.split('-').map(part => parseInt(part, 10));
          // Format the date in a more readable format (e.g., "5/9/2025")
          const formattedDate = `${month}/${day}/${year}`;

          newForecast.push({
            date: formattedDate,
            condition: getConditionFromWeatherCode(weatherCode),
            maxTemperature: maxTemp,
            minTemperature: minTemp,
            windSpeed,
            precipitation
          });
        }
      }

      setWeatherData(newWeatherData);
      setForecast(newForecast);
      setSelectedLocation(location);
      setShowLocationModal(false);
    } catch (err) {
      setError('Failed to fetch weather data. Please try again.');
      console.error('Error fetching weather data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create a debounced version of the search function to avoid too many API calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchLocations = useCallback(
    debounce((query: string) => {
      searchLocationsImpl(query);
    }, 500),
    []
  );

  // Initialize with a default location when component mounts
  useEffect(() => {
    // Default to San Francisco as an example
    const defaultLocation: Location = {
      name: 'San Francisco, California, United States',
      latitude: 37.7749,
      longitude: -122.4194
    };

    fetchWeatherData(defaultLocation);
  }, []);

  // Function to get status color
  const getStatusColor = (status: string): 'success' | 'warning' | 'danger' => {
    switch (status) {
      case 'safe':
        return 'success';
      case 'caution':
        return 'warning';
      case 'unsafe':
        return 'danger';
      default:
        return 'warning';
    }
  };

  // Function to get weather icon
  const getWeatherIcon = (condition: string) => {
    if (condition.toLowerCase().includes('sun') || condition.toLowerCase().includes('clear')) {
      return <WbSunnyIcon />;
    }
    if (condition.toLowerCase().includes('rain') || condition.toLowerCase().includes('shower')) {
      return <OpacityIcon />;
    }
    return <CloudIcon />;
  };

  // Function to handle location search
  const handleLocationSearch = () => {
    setShowLocationModal(true);
  };

  // Function to handle location selection
  const handleLocationSelect = (location: Location) => {
    fetchWeatherData(location);
  };

  // Render forecast item
  const renderForecastItem = (item: ForecastData) => (
    <Box key={item.date} sx={{ textAlign: 'center', p: 1 }}>
      <Typography level="body-sm" fontWeight="bold">{item.date}</Typography>
      <Box sx={{ fontSize: '1.5rem', color: 'primary.500', display: 'flex', justifyContent: 'center', my: 1 }}>
        {getWeatherIcon(item.condition)}
      </Box>
      <Typography level="body-sm">{item.condition}</Typography>
      <Typography level="body-sm" sx={{ color: 'danger.500' }}>
        H: {item.maxTemperature.toFixed(1)}°C / {celsiusToFahrenheit(item.maxTemperature).toFixed(1)}°F
      </Typography>
      <Typography level="body-sm" sx={{ color: 'primary.500' }}>
        L: {item.minTemperature.toFixed(1)}°C / {celsiusToFahrenheit(item.minTemperature).toFixed(1)}°F
      </Typography>
      <Typography level="body-xs">{item.windSpeed} km/h</Typography>
      <Typography level="body-xs">{item.precipitation}% {t('dashboard.precipitation_short', 'Precip.')}</Typography>
    </Box>
  );

  return (
    <Card variant="outlined">
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography level="title-md">{t('dashboard.weather_impact', 'Weather Impact Indicator')}</Typography>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<LocationOnIcon />}
          size="sm"
          onClick={handleLocationSearch}
        >
          {selectedLocation ? selectedLocation.name : t('dashboard.select_location', 'Select Location')}
        </Button>
      </Box>

      <CardContent>
        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {!loading && error && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="danger">{error}</Typography>
            <Button
              onClick={() => setError(null)}
              size="sm"
              sx={{ mt: 1 }}
            >
              {t('dashboard.dismiss', 'Dismiss')}
            </Button>
          </Box>
        )}

        {/* Content state */}
        {!loading && !error && (
          <>
            <Grid container spacing={2}>
              {/* Main weather display */}
              <Grid xs={12} md={6}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ fontSize: '2.5rem', color: 'primary.500', display: 'flex' }}>
                      {getWeatherIcon(weatherData.currentCondition)}
                    </Box>
                    <Box>
                      <Typography level="title-lg">{weatherData.currentCondition}</Typography>
                      <Typography level="body-md">
                        {selectedLocation
                          ? `${t('dashboard.current_conditions', 'Current Conditions')} - ${selectedLocation.name}`
                          : t('dashboard.current_conditions', 'Current Conditions')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Chip
                    color={getStatusColor(weatherData.flightStatus)}
                    size="lg"
                    variant="soft"
                    sx={{ width: 'fit-content' }}
                  >
                    {(() => {
                      if (weatherData.flightStatus === 'safe') return t('dashboard.flight_safe', 'Flight Conditions: Safe');
                      if (weatherData.flightStatus === 'caution') return t('dashboard.flight_caution', 'Flight Conditions: Use Caution');
                      return t('dashboard.flight_unsafe', 'Flight Conditions: Unsafe');
                    })()}
                  </Chip>

                  <Grid container spacing={1}>
                    <Grid xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ThermostatIcon fontSize="small" sx={{ color: 'text.tertiary' }} />
                        <Typography level="body-sm">
                          {weatherData.temperature}°C / {celsiusToFahrenheit(weatherData.temperature).toFixed(1)}°F
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AirIcon fontSize="small" sx={{ color: 'text.tertiary' }} />
                        <Typography level="body-sm">
                          {weatherData.windSpeed} km/h {weatherData.windDirection}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <OpacityIcon fontSize="small" sx={{ color: 'text.tertiary' }} />
                        <Typography level="body-sm">
                          {weatherData.humidity}% {t('dashboard.humidity', 'Humidity')}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CloudIcon fontSize="small" sx={{ color: 'text.tertiary' }} />
                        <Typography level="body-sm">
                          {weatherData.precipitation}% {t('dashboard.precipitation', 'Precipitation')}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>

              {/* 3-Day Forecast - Moved up */}
              <Grid xs={12} md={6}>
                {forecast.length > 0 && (
                  <Box>
                    <Typography level="body-md" fontWeight="bold" mb={1}>
                      {t('dashboard.forecast', '3-Day Forecast')}
                    </Typography>
                    <Grid container spacing={1}>
                      {forecast.map((item) => (
                        <Grid key={item.date} xs={4}>
                          {renderForecastItem(item)}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Recent Location Conditions - Moved below forecast */}
            <Box sx={{ mt: 3 }}>
              <Typography level="body-md" fontWeight="bold" mb={1}>
                {t('dashboard.recent_location_conditions', 'Recent Location Conditions')}
              </Typography>
              <Stack spacing={1} divider={<Divider />}>
                {weatherData.locations.map((location, index) => (
                  <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography level="body-sm">{location.name}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ color: 'text.tertiary', display: 'flex', alignItems: 'center' }}>
                          {getWeatherIcon(location.condition)}
                        </Box>
                        <Typography level="body-xs">
                          {location.condition}, {location.windSpeed} km/h
                        </Typography>
                      </Stack>
                    </Box>
                    <Chip
                      color={getStatusColor(location.status)}
                      size="sm"
                      variant="soft"
                    >
                      {(() => {
                        if (location.status === 'safe') return t('dashboard.safe', 'Safe');
                        if (location.status === 'caution') return t('dashboard.caution', 'Caution');
                        return t('dashboard.unsafe', 'Unsafe');
                      })()}
                    </Chip>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>

      {/* Location Selection Modal */}
      <Modal
        open={showLocationModal}
        onClose={() => setShowLocationModal(false)}
      >
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">
            {t('dashboard.select_location', 'Select Location')}
          </Typography>

          <Box sx={{ my: 2 }}>
            <FormControl>
              <FormLabel>{t('dashboard.search_location', 'Search for a location')}</FormLabel>
              <Input
                placeholder={t('dashboard.enter_city', 'Enter city name')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchLocations(e.target.value);
                }}
                startDecorator={<SearchIcon />}
                endDecorator={
                  searchLoading ? <CircularProgress size="sm" /> : null
                }
              />
            </FormControl>
          </Box>

          {searchResults.length > 0 && (
            <>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                {t('dashboard.search_results', 'Search Results')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '300px', overflowY: 'auto' }}>
                {searchResults.map((location) => (
                  <Button
                    key={location.name}
                    variant="soft"
                    color="neutral"
                    fullWidth
                    startDecorator={<LocationOnIcon />}
                    onClick={() => handleLocationSelect(location)}
                  >
                    {location.name}
                  </Button>
                ))}
              </Box>
            </>
          )}

          {searchQuery && searchResults.length === 0 && !searchLoading && (
            <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', my: 2 }}>
              {t('dashboard.no_results', 'No locations found. Try a different search term.')}
            </Typography>
          )}

          <Typography level="body-sm" sx={{ mt: 3, textAlign: 'center', color: 'neutral.500' }}>
            {t('dashboard.location_note', 'Note: Location data will be stored in the database for future use.')}
          </Typography>
        </ModalDialog>
      </Modal>
    </Card>
  );
};

export default WeatherImpactIndicator;
